import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/*
 * Quick Buy speed layer (ATW).
 *
 * - The modal opens immediately with a light loading shell; the product content is
 *   rendered as soon as the (shared) product grid is available.
 * - One module-level cache + in-flight map is shared by every quick-add-component,
 *   so a prefetch from one card serves the real click from any other card.
 * - A global open token guarantees that a late response can never overwrite a newer
 *   (or closed) modal.
 * - Controlled prefetch: viewport proximity (IntersectionObserver + dwell) and
 *   desktop hover / keyboard focus, max 2 concurrent low-priority requests,
 *   hard cap per page view, disabled for Save-Data / 2g connections.
 */

const FETCH_TIMEOUT_MS = 15000;
const CACHE_LIMIT = 24;
const PREFETCH_CONCURRENCY = 2;
const PREFETCH_QUEUE_LIMIT = 8;
const PREFETCH_TOTAL_LIMIT = 16;
const PREFETCH_DWELL_MS = 350;
const PREFETCH_HOVER_MS = 120;
const SKELETON_STYLE_ID = 'atw-qa-skeleton-style';

/** @type {Map<string, Element>} url -> product grid (pristine, always cloned before use) */
const sharedCache = new Map();
/** @type {Map<string, { controller: AbortController, clickOwned: boolean, promise: Promise<Element | null> }>} */
const inflight = new Map();
/** Incremented when the cache is cleared so responses started before a cart update are not stored. */
let cacheEpoch = 0;
/** Incremented on every Quick Buy click and every dialog close: only the latest token may render. */
let openToken = 0;
let prefetchStarted = 0;

const prefetchQueue = [];
const prefetchActive = new Set();

function connectionAllowsPrefetch() {
  if (window.Shopify?.designMode) return false;
  const connection = /** @type {any} */ (navigator).connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !/^(slow-)?2g$/.test(connection.effectiveType || '');
}

function whenIdle() {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(undefined), { timeout: 1500 });
    else setTimeout(resolve, 50);
  });
}

function clearQuickAddCache() {
  sharedCache.clear();
  cacheEpoch++;
}

document.addEventListener(StandardEvents.cartLinesUpdate, clearQuickAddCache);
// Prefetch is paused while the tab is hidden: resume the queue when it becomes visible again.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) pumpPrefetch();
});

/**
 * Loads (or reuses) the product grid for a URL. Never rejects: resolves null on failure.
 * @param {string} url
 * @param {{ clickOwned?: boolean, low?: boolean }} [options]
 * @returns {Promise<Element | null>}
 */
function loadProductGrid(url, { clickOwned = false, low = false } = {}) {
  const cached = sharedCache.get(url);
  if (cached) return Promise.resolve(cached);

  const existing = inflight.get(url);
  if (existing) {
    if (clickOwned) existing.clickOwned = true;
    return existing.promise;
  }

  const controller = new AbortController();
  const epoch = cacheEpoch;
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  const entry = { controller, clickOwned, promise: /** @type {Promise<Element | null>} */ (Promise.resolve(null)) };

  entry.promise = (async () => {
    try {
      /** @type {RequestInit & { priority?: string }} */
      const init = { signal: controller.signal };
      if (low) init.priority = 'low';
      const response = await fetch(url, init);
      if (!response.ok) throw new Error(`Quick Buy: HTTP ${response.status}`);

      const text = await response.text();
      if (low) await whenIdle();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const grid = doc.querySelector('[data-product-grid-content]');
      if (!grid) return null;

      const stored = /** @type {Element} */ (grid.cloneNode(true));
      if (epoch === cacheEpoch) {
        sharedCache.set(url, stored);
        while (sharedCache.size > CACHE_LIMIT) sharedCache.delete(sharedCache.keys().next().value);
      }
      return stored;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
      if (inflight.get(url) === entry) inflight.delete(url);
    }
  })();

  inflight.set(url, entry);
  return entry.promise;
}

/** A new click supersedes any other click-owned request: free the bandwidth for the product the shopper wants now. */
function abortOtherClickFetches(keepUrl) {
  for (const [url, entry] of inflight) {
    if (url !== keepUrl && entry.clickOwned) entry.controller.abort();
  }
}

function pumpPrefetch() {
  if (!connectionAllowsPrefetch() || document.hidden) return;
  for (const entry of inflight.values()) if (entry.clickOwned) return;

  while (prefetchActive.size < PREFETCH_CONCURRENCY && prefetchQueue.length && prefetchStarted < PREFETCH_TOTAL_LIMIT) {
    const url = prefetchQueue.shift();
    if (!url || sharedCache.has(url) || inflight.has(url)) continue;

    prefetchStarted++;
    prefetchActive.add(url);
    loadProductGrid(url, { low: true }).finally(() => {
      prefetchActive.delete(url);
      setTimeout(pumpPrefetch, 150);
    });
  }
}

/**
 * @param {string} url
 * @param {{ front?: boolean }} [options]
 */
function enqueuePrefetch(url, { front = false } = {}) {
  if (!url || !connectionAllowsPrefetch()) return;
  if (sharedCache.has(url) || inflight.has(url)) return;

  const index = prefetchQueue.indexOf(url);
  if (index !== -1) {
    if (!front) return;
    prefetchQueue.splice(index, 1);
  }

  if (front) prefetchQueue.unshift(url);
  else prefetchQueue.push(url);
  while (prefetchQueue.length > PREFETCH_QUEUE_LIMIT) prefetchQueue.pop();

  pumpPrefetch();
}

function dequeuePrefetch(url) {
  const index = prefetchQueue.indexOf(url);
  if (index !== -1) prefetchQueue.splice(index, 1);
}

/** @type {WeakMap<Element, QuickAddComponent>} */
const prefetchTargets = new WeakMap();
/** @type {WeakMap<Element, number>} */
const dwellTimers = new WeakMap();
/** @type {IntersectionObserver | null} */
let prefetchObserver = null;

function getPrefetchObserver() {
  if (prefetchObserver || !('IntersectionObserver' in window)) return prefetchObserver;

  prefetchObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const component = prefetchTargets.get(entry.target);
        if (!component) continue;

        clearTimeout(dwellTimers.get(entry.target));
        if (entry.isIntersecting) {
          // Dwell first: fast scrolling / swiping past a card must not trigger a request.
          dwellTimers.set(
            entry.target,
            window.setTimeout(() => enqueuePrefetch(component.prefetchUrl), PREFETCH_DWELL_MS)
          );
        } else {
          dequeuePrefetch(component.prefetchUrl);
        }
      }
    },
    { rootMargin: '200px 300px' }
  );

  return prefetchObserver;
}

function injectSkeletonStyles() {
  if (document.getElementById(SKELETON_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SKELETON_STYLE_ID;
  style.textContent = `
    .atw-qa-skel{grid-column:1/-1;width:100%;min-height:340px;display:flex;flex-direction:column;gap:16px}
    .atw-qa-skel__head{display:flex;gap:12px;align-items:flex-start}
    .atw-qa-skel__thumb{flex:0 0 80px;width:80px;aspect-ratio:1;overflow:hidden;background:color-mix(in srgb,currentColor 6%,transparent)}
    .atw-qa-skel__thumb img{display:block;width:100%;height:100%;object-fit:cover}
    .atw-qa-skel__meta{display:flex;flex-direction:column;gap:10px;min-width:0;padding-top:2px}
    .atw-qa-skel__title{font-size:1rem;line-height:1.3;font-weight:500}
    .atw-qa-skel__bar,.atw-qa-skel__block,.atw-qa-skel__btn{display:block;background:color-mix(in srgb,currentColor 7%,transparent);animation:atw-qa-pulse 1.3s ease-in-out infinite}
    .atw-qa-skel__bar{height:12px;width:90px}
    .atw-qa-skel__block{height:92px}
    .atw-qa-skel__btn{height:52px}
    .atw-qa-skel__sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
    @keyframes atw-qa-pulse{0%,100%{opacity:1}50%{opacity:.55}}
    @media (prefers-reduced-motion:reduce){.atw-qa-skel__bar,.atw-qa-skel__block,.atw-qa-skel__btn{animation:none}}
  `;
  document.head.appendChild(style);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Element | null} */
  #observedCard = null;
  /** @type {number | undefined} */
  #hoverTimer;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) {
      if (productCard.closest('.atw-search-results')) {
        return productCard.querySelector('[ref="productCardLink"]')?.href || '';
      }
      // The ATW universal card can be inserted after the section script has run
      // (search pagination and cart recommendations). Keep the real product link
      // as a safe source for the shared Quick Add flow.
      return productCard.productPageUrl || productCard.querySelector('[ref="productCardLink"]')?.href || '';
    }

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * URL to prefetch for this card, or '' when this card must not be prefetched
   * (direct-add products, selling plans, no modal button, Save-Data).
   * @returns {string}
   */
  get prefetchUrl() {
    if (!connectionAllowsPrefetch()) return '';
    if (this.dataset.usesSellingPlans === 'true') return '';
    if (this.dataset.quickAddButton === 'add') return '';
    if (!this.querySelector('.quick-add__button--choose')) return '';
    return this.productPageUrl;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    this.#setupPrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    this.#teardownPrefetch();
  }

  #setupPrefetch() {
    if (!this.querySelector('.quick-add__button--choose')) return;

    const card = this.closest('product-card') ?? this;
    this.#observedCard = card;
    prefetchTargets.set(card, this);
    getPrefetchObserver()?.observe(card);

    // Desktop intent: mouse hover on the card, keyboard focus on the button.
    card.addEventListener('pointerenter', this.#handlePointerEnter);
    card.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
  }

  #teardownPrefetch() {
    const card = this.#observedCard;
    if (!card) return;

    clearTimeout(dwellTimers.get(card));
    clearTimeout(this.#hoverTimer);
    prefetchObserver?.unobserve(card);
    prefetchTargets.delete(card);
    card.removeEventListener('pointerenter', this.#handlePointerEnter);
    card.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.#observedCard = null;
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => enqueuePrefetch(this.prefetchUrl, { front: true }), PREFETCH_HOVER_MS);
  };

  #handlePointerLeave = () => {
    clearTimeout(this.#hoverTimer);
  };

  #handleFocusIn = () => {
    if (!this.matches(':has(:focus-visible)')) return;
    enqueuePrefetch(this.prefetchUrl, { front: true });
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Renders a product grid into the modal (fresh clone, cache stays pristine).
   * @param {Element} productGrid
   */
  async #renderGrid(productGrid) {
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);
  }

  /**
   * Light loading shell inside the existing modal: real title from the card plus quiet placeholders.
   * @param {HTMLElement} modalContent
   */
  #showLoading(modalContent) {
    injectSkeletonStyles();

    const card = this.closest('product-card');
    const cardImage = card?.querySelector('img');
    const imageSrc = cardImage?.currentSrc || cardImage?.getAttribute('src') || '';

    const shell = document.createElement('div');
    shell.className = 'atw-qa-skel';
    shell.setAttribute('role', 'status');

    const head = document.createElement('div');
    head.className = 'atw-qa-skel__head';
    const thumb = document.createElement('div');
    thumb.className = 'atw-qa-skel__thumb';
    if (imageSrc) {
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.src = imageSrc;
      thumb.appendChild(img);
    }
    const meta = document.createElement('div');
    meta.className = 'atw-qa-skel__meta';
    const title = document.createElement('span');
    title.className = 'atw-qa-skel__title';
    // The loaded modal shows the short product name (the card title carries a " | type" suffix): keep the shell identical to avoid a visible text jump.
    title.textContent = (this.dataset.productTitle || '').split(' | ')[0];
    const priceBar = document.createElement('i');
    priceBar.className = 'atw-qa-skel__bar';
    meta.append(title, priceBar);
    head.append(thumb, meta);

    const variants = document.createElement('i');
    variants.className = 'atw-qa-skel__block';
    const addButton = document.createElement('i');
    addButton.className = 'atw-qa-skel__btn';
    const sr = document.createElement('span');
    sr.className = 'atw-qa-skel__sr';
    sr.textContent = 'Φόρτωση προϊόντος…';

    shell.append(head, variants, addButton, sr);

    modalContent.removeAttribute('view-event-payload');
    modalContent.setAttribute('aria-busy', 'true');
    modalContent.replaceChildren(shell);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const token = ++openToken;
    performance.mark('atw-qa:click');
    abortOtherClickFetches(currentUrl);

    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!modalContent || !(dialogComponent instanceof QuickAddDialog)) {
      window.location.href = currentUrl;
      return;
    }

    // Cached / prefetched: render first, open with the content already in place.
    const cached = sharedCache.get(currentUrl);
    if (cached) {
      modalContent.removeAttribute('aria-busy');
      await this.#renderGrid(cached);
      if (token !== openToken) return;
      this.#openQuickAddModal();
      performance.mark('atw-qa:content');
      return;
    }

    // Not cached: open the modal right away with a light loading shell, fill it when the grid arrives.
    this.#showLoading(modalContent);
    this.#openQuickAddModal();
    performance.mark('atw-qa:shell');

    const productGrid = await loadProductGrid(currentUrl, { clickOwned: true });

    // Superseded by another click, or the modal was closed meanwhile: render nothing, reopen nothing.
    if (token !== openToken) return;

    if (!productGrid) {
      // A failed fetch must never leave a frozen shell or show another product: go to the real product page.
      window.location.href = currentUrl;
      return;
    }

    await this.#renderGrid(productGrid);
    if (token !== openToken) return;

    modalContent.removeAttribute('aria-busy');
    this.#resetScroll();
    performance.mark('atw-qa:content');
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const dialog = dialogComponent.refs?.dialog;
    // Already open (or opening): the content was swapped in place, nothing else to do.
    if (!dialog || dialog.open || dialogComponent.isOpening) return;
    dialogComponent.markOpening();

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    document.dispatchEvent(new CustomEvent('atw:overlay-opening', { detail: { type: 'quick-add' } }));
    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content (kept for API compatibility; Quick Buy uses the shared loader).
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const trademarkNotice = productGrid.querySelector('.atw-buy-tm-note');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (trademarkNotice) {
        productGrid.appendChild(trademarkNotice);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    // Always insert into an empty container: a fresh insert is far cheaper than diffing the previous
    // product's DOM, and it also drops the loading shell / any stale interactive state.
    modalContent.replaceChildren();

    morph(modalContent, productGrid);

    this.#placeTrademarkNoticeAfterPurchase(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Places the trademark notice immediately after the purchase actions in Quick Buy.
   * @param {Element} modalContent
   */
  #placeTrademarkNoticeAfterPurchase(modalContent) {
    const notice = modalContent.querySelector('.atw-buy-tm-note');
    if (!notice) return;

    if (isMobileBreakpoint()) {
      const productForm = modalContent.querySelector('product-form-component');
      if (productForm) productForm.insertAdjacentElement('afterend', notice);
      return;
    }

    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    if (buyButtons) buyButtons.insertAdjacentElement('afterend', notice);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #openedAt = 0;
  #opening = false;

  constructor() {
    super();

    // Any real close (button, Escape, outside click, cart success, breakpoint) invalidates in-flight Quick Buy renders immediately.
    // A close request while the dialog is not open yet is a no-op (as in the base class) and must not invalidate the pending open.
    const baseClose = this.closeDialog;
    this.closeDialog = (...args) => {
      if (this.refs?.dialog?.open) openToken++;
      return baseClose(...args);
    };
  }

  get isOpening() {
    return this.#opening;
  }

  markOpening() {
    this.#opening = true;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    // A second tap of a double tap would land on the backdrop and close the sheet we just opened.
    this.addEventListener('click', this.#guardEarlyBackdropClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('click', this.#guardEarlyBackdropClick, true);
  }

  #handleDialogOpen = () => {
    this.#openedAt = performance.now();
  };

  /** @param {Event} event */
  #guardEarlyBackdropClick = (event) => {
    if (performance.now() - this.#openedAt < 450 && event.target === this.refs.dialog) {
      event.stopImmediatePropagation();
    }
  };

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    this.#opening = false;
    openToken++;

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
